import React from 'react';
import { Box, Chip, Divider, LinearProgress, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useScrabbleGameContext } from './context/ScrabbleGameContext';
import { GAME_CONFIG } from './config/gameConfig';
import GameActionBar from './GameActionBar';

function ScorePanel({
  isInventoryOpen = false,
  onToggleInventory,
  inspectedRackTile,
  inspectedTileModifiers = [],
  inspectedTileBaseScore = 0,
  inspectedTileDisplayScore = 0,
  inspectedTileScoreBonus = 0,
  inspectedBoardSquareModifiers = [],
}) {
  const {
    playerName,
    score,
    scorePreview,
    stagedBoard,
    targetScore,
    words,
    history,
    roundIndex,
    roundTileTarget,
    tilesPlacedThisRound,
    dictionaryError,
    submitMessage,
    structureErrors,
  } = useScrabbleGameContext();

  const helperMessage = dictionaryError || submitMessage || structureErrors[0] || '';

  const isInspectingTile = Boolean(inspectedRackTile?.letter);
  const currentScore = Number(score || 0);
  const previewGain = Number(scorePreview || 0);
  const projectedScore = currentScore + previewGain;
  const previewGainLabel = `${previewGain > 0 ? '+' : ''}${previewGain}`;
  const roundTarget = Math.max(0, Number(targetScore || 0));
  const targetProgress = roundTarget > 0 ? Math.min(100, Math.max(0, (projectedScore / roundTarget) * 100)) : 0;
  const isScoreTargetReached = roundTarget > 0 && projectedScore >= roundTarget;
  const tileTarget = Math.max(0, Number(roundTileTarget || 0));
  const tilesPlaced = Number(tilesPlacedThisRound || 0);
  const stagedTileCount = Object.keys(stagedBoard || {}).length;
  const projectedTilesPlaced = tilesPlaced + stagedTileCount;
  const tileProgress = tileTarget > 0 ? Math.min(100, Math.max(0, (projectedTilesPlaced / tileTarget) * 100)) : 0;
  const isTileTargetReached = tileTarget > 0 && projectedTilesPlaced >= tileTarget;
  const isTargetReached = isScoreTargetReached && isTileTargetReached;
  const { roundsPerAct } = GAME_CONFIG.actConfig;
  const actIndex = Math.ceil(roundIndex / roundsPerAct);
  const roundInAct = ((roundIndex - 1) % roundsPerAct) + 1;



  const formatModifierLabels = (modifiers = []) => {
    const labels = modifiers.map((modifier) => modifier.label).filter(Boolean);
    return labels.length ? labels.join(' × ') : 'none';
  };

  const formatEffectLabels = (effects = []) => {
    const labels = effects.map((effect) => `${effect.label}: ${effect.description}`).filter(Boolean);
    return labels.length ? labels.join(' | ') : 'none';
  };

  return (
    <Box
      sx={{
        padding: { xs: 1.2, sm: 1.5 },
        borderRadius: 3,
        backgroundColor: 'background.paper',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        width: '100%',
        maxHeight: { xs: 'none', lg: '100%' },
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.2,
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.9 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
            Act {actIndex} — Round {roundInAct}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 500 }}>
            of {roundsPerAct}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.4}>
          {Array.from({ length: roundsPerAct }, (_, i) => {
            const roundInActIndex = (actIndex - 1) * roundsPerAct + i;
            const isPast = roundInActIndex < roundIndex - 1;
            const isCurrent = roundInActIndex === roundIndex - 1;
            return (
              <Box
                key={i}
                sx={{
                  height: 5,
                  flex: isCurrent ? 2 : 1,
                  borderRadius: 3,
                  backgroundColor: isPast || isCurrent ? 'primary.main' : 'divider',
                  opacity: isPast ? 0.35 : 1,
                  transition: 'flex 0.2s ease, opacity 0.2s ease',
                }}
              />
            );
          })}
        </Stack>
      </Box>

      <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
        <GameActionBar
          isInventoryOpen={isInventoryOpen}
          onToggleInventory={onToggleInventory}
        />
      </Box>

      <Divider />

      <Box
        sx={{
          borderRadius: 2.4,
          border: (theme) =>
            `1px solid ${
              isTargetReached
                ? alpha(theme.palette.warning.main, 0.32)
                : alpha(theme.palette.primary.main, 0.24)
            }`,
          background: (theme) =>
            `linear-gradient(145deg, ${
              isTargetReached
                ? alpha(theme.palette.warning.light, 0.22)
                : alpha(theme.palette.primary.light, 0.14)
            } 0%, ${alpha(theme.palette.background.paper, 0.97)} 52%)`,
          boxShadow: (theme) =>
            `0 8px 18px ${
              isTargetReached
                ? alpha(theme.palette.warning.main, 0.11)
                : alpha(theme.palette.primary.main, 0.08)
            }`,
          padding: 1.3,
        }}
      >
        <Stack spacing={1.15}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Chip
              size="small"
              label={previewGainLabel}
              sx={{
                borderRadius: 1,
                fontWeight: 800,
                minWidth: 54,
                color: previewGain < 0 ? 'error.dark' : 'success.dark',
                backgroundColor: (theme) =>
                  previewGain < 0
                    ? alpha(theme.palette.error.main, 0.16)
                    : alpha(theme.palette.success.main, 0.2),
              }}
            />
            <Stack alignItems="flex-end" spacing={0}>
              <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 600 }}>
                After submit: {projectedScore}
              </Typography>
            </Stack>
          </Stack>

          <Stack spacing={0.55}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase' }}>
                Score
              </Typography>
              <Typography sx={{ fontSize: 10, color: isScoreTargetReached ? 'warning.main' : 'text.secondary', fontWeight: 600 }}>
                {projectedScore} / {roundTarget}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={targetProgress}
              sx={{
                height: 7,
                borderRadius: 999,
                backgroundColor: (theme) =>
                  isScoreTargetReached
                    ? alpha(theme.palette.warning.main, 0.2)
                    : alpha(theme.palette.primary.main, 0.14),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 999,
                  backgroundColor: isScoreTargetReached ? 'warning.main' : 'primary.main',
                },
              }}
            />
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.4 }}>
              <Typography sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase' }}>
                Tiles
              </Typography>
              <Typography sx={{ fontSize: 10, color: isTileTargetReached ? 'warning.main' : 'text.secondary', fontWeight: 600 }}>
                {projectedTilesPlaced} / {tileTarget}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={tileProgress}
              sx={{
                height: 7,
                borderRadius: 999,
                backgroundColor: (theme) =>
                  isTileTargetReached
                    ? alpha(theme.palette.warning.main, 0.2)
                    : alpha(theme.palette.secondary.main, 0.14),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 999,
                  backgroundColor: isTileTargetReached ? 'warning.main' : 'secondary.main',
                },
              }}
            />
          </Stack>

          <Stack direction="row" spacing={0.8}>
            <Box
              sx={{
                flex: 1,
                borderRadius: 1.6,
                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.95)}`,
                backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.9),
                padding: 1,
              }}
            >
              <Typography sx={{ fontSize: 10, textTransform: 'uppercase', color: 'text.secondary' }}>
                Current Score
              </Typography>
              <Typography sx={{ fontSize: 27, fontWeight: 800, lineHeight: 1.06 }}>
                {currentScore}
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{playerName}</Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                borderRadius: 1.6,
                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.95)}`,
                backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.9),
                padding: 1,
              }}
            >
              <Typography sx={{ fontSize: 10, textTransform: 'uppercase', color: 'text.secondary' }}>
                Round Target
              </Typography>
              <Typography sx={{ fontSize: 27, fontWeight: 800, lineHeight: 1.06 }}>
                {roundTarget}
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Tiles: {projectedTilesPlaced}/{tileTarget}</Typography>
            </Box>
          </Stack>
        </Stack>
      </Box>

      {helperMessage ? (
        <Typography
          sx={{
            fontSize: 12,
            color: dictionaryError ? 'error.main' : 'text.secondary',
          }}
        >
          {helperMessage}
        </Typography>
      ) : null}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          paddingRight: 0.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.2,
        }}
      >
        {/* Tile Info — shown above Score Breakdown when a tile is inspected */}
        {inspectedRackTile ? (
          <>
            <Divider />
            <Typography variant="subtitle2">Tile Info</Typography>
            <Stack spacing={0.75}>
              {isInspectingTile ? (
                <>
                  {/* Tile header */}
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: 0.75,
                        backgroundColor: (theme) => theme.palette.scrabble?.tile,
                        border: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.22)}`,
                        boxShadow: (theme) =>
                          `0 1px 0 ${alpha(theme.palette.text.primary, 0.24)}, 0 3px 5px ${alpha(theme.palette.text.primary, 0.14)}`,
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Typography sx={{ fontWeight: 900, fontSize: 15, lineHeight: 1 }}>
                        {inspectedRackTile.letter}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 14, letterSpacing: 0.1 }}>
                      Tile {inspectedRackTile.letter}
                    </Typography>
                  </Stack>

                  {/* Score + Word × stat cards */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.65 }}>
                    <Box
                      sx={{
                        borderRadius: 1.6,
                        border: (theme) =>
                          `1px solid ${inspectedTileScoreBonus !== 0 ? alpha(theme.palette.success.main, 0.38) : alpha(theme.palette.divider, 0.95)}`,
                        background: (theme) =>
                          inspectedTileScoreBonus !== 0
                            ? `linear-gradient(145deg, ${alpha(theme.palette.success.light, 0.18)} 0%, ${alpha(theme.palette.background.paper, 0.97)} 65%)`
                            : alpha(theme.palette.background.paper, 0.9),
                        px: 1,
                        py: 0.9,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 9.5,
                          fontWeight: 700,
                          color: 'text.secondary',
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          mb: 0.45,
                        }}
                      >
                        Score
                      </Typography>
                      {inspectedTileScoreBonus !== 0 ? (
                        <Stack spacing={0.3}>
                          <Stack direction="row" spacing={0.5} alignItems="baseline">
                            <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1 }}>
                              {inspectedTileBaseScore}
                            </Typography>
                            <Typography sx={{ fontSize: 9, color: 'text.secondary', lineHeight: 1 }}>→</Typography>
                            <Typography sx={{ fontSize: 20, fontWeight: 900, lineHeight: 1 }}>
                              {inspectedTileDisplayScore}
                            </Typography>
                          </Stack>
                          <Chip
                            size="small"
                            label={`${inspectedTileScoreBonus > 0 ? '+' : ''}${inspectedTileScoreBonus}`}
                            sx={{
                              alignSelf: 'flex-start',
                              height: 17,
                              fontSize: 9.5,
                              fontWeight: 800,
                              borderRadius: 0.6,
                              color: 'success.dark',
                              backgroundColor: (theme) => alpha(theme.palette.success.main, 0.16),
                            }}
                          />
                        </Stack>
                      ) : (
                        <Typography sx={{ fontSize: 20, fontWeight: 900, lineHeight: 1 }}>
                          {inspectedTileDisplayScore}
                        </Typography>
                      )}
                    </Box>

                    {(() => {
                      const mult = Number(inspectedRackTile?.modifiers?.multiplier);
                      const hasMultiplier = mult > 1;
                      return (
                        <Box
                          sx={{
                            borderRadius: 1.6,
                            border: (theme) =>
                              `1px solid ${hasMultiplier ? alpha(theme.palette.success.main, 0.38) : alpha(theme.palette.divider, 0.95)}`,
                            background: (theme) =>
                              hasMultiplier
                                ? `linear-gradient(145deg, ${alpha(theme.palette.success.light, 0.18)} 0%, ${alpha(theme.palette.background.paper, 0.97)} 65%)`
                                : alpha(theme.palette.background.paper, 0.9),
                            px: 1,
                            py: 0.9,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 9.5,
                              fontWeight: 700,
                              color: 'text.secondary',
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                              mb: 0.45,
                            }}
                          >
                            Word ×
                          </Typography>
                          {hasMultiplier ? (
                            <Typography
                              sx={{ fontSize: 20, fontWeight: 900, lineHeight: 1, color: 'success.dark' }}
                            >
                              ×{mult}
                            </Typography>
                          ) : (
                            <Typography
                              sx={{
                                fontSize: 15,
                                fontWeight: 700,
                                color: 'text.secondary',
                                opacity: 0.3,
                                lineHeight: 1,
                              }}
                            >
                              —
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                  </Box>

                  {/* Extras modifiers */}
                  {(() => {
                    const extrasModifiers = inspectedTileModifiers.filter(
                      (m) => m.id !== 'score-bonus' && m.id !== 'tile-multiplier'
                    );
                    if (!extrasModifiers.length) return null;
                    return (
                      <Box
                        sx={{
                          borderRadius: 1.6,
                          border: (theme) => `1px solid ${alpha(theme.palette.secondary.main, 0.28)}`,
                          background: (theme) =>
                            `linear-gradient(145deg, ${alpha(theme.palette.secondary.light, 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.97)} 65%)`,
                          overflow: 'hidden',
                        }}
                      >
                        {extrasModifiers.map((modifier, index) => (
                          <Stack
                            key={`${inspectedRackTile.id}-${modifier.id}`}
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                            sx={{
                              px: 1,
                              py: 0.7,
                              borderTop: index > 0
                                ? (theme) => `1px solid ${alpha(theme.palette.secondary.main, 0.14)}`
                                : 'none',
                            }}
                          >
                            <Chip
                              size="small"
                              label={modifier.label}
                              color="secondary"
                              variant="outlined"
                              sx={{
                                height: 18,
                                fontSize: 9.5,
                                fontWeight: 800,
                                borderRadius: 0.6,
                                flexShrink: 0,
                                mt: 0.15,
                              }}
                            />
                            <Typography sx={{ fontSize: 10.5, color: 'text.secondary', lineHeight: 1.4 }}>
                              {modifier.description}
                            </Typography>
                          </Stack>
                        ))}
                      </Box>
                    );
                  })()}
                </>
              ) : null}

              {/* Board square section */}
              <Stack direction="row" spacing={0.8} alignItems="center">
                <Typography sx={{ fontWeight: 700, fontSize: 12.5 }}>
                  {inspectedRackTile?.boardSquareCoord
                    ? `Square ${inspectedRackTile.boardSquareCoord}`
                    : 'Board Square'}
                </Typography>
                <Chip
                  size="small"
                  label={`${inspectedBoardSquareModifiers.length} modifier${
                    inspectedBoardSquareModifiers.length === 1 ? '' : 's'
                  }`}
                  sx={{ height: 18, fontSize: 10, fontWeight: 700, borderRadius: 0.7 }}
                />
              </Stack>

              {!inspectedBoardSquareModifiers.length ? (
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                  No modifiers on this square.
                </Typography>
              ) : (
                <Box
                  sx={{
                    borderRadius: 1.6,
                    border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
                    background: (theme) =>
                      `linear-gradient(145deg, ${alpha(theme.palette.primary.light, 0.12)} 0%, ${alpha(theme.palette.background.paper, 0.97)} 65%)`,
                    overflow: 'hidden',
                  }}
                >
                  {inspectedBoardSquareModifiers.map((modifier, index) => (
                    <Stack
                      key={`${inspectedRackTile.id}-board-${modifier.id}`}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                      sx={{
                        px: 1,
                        py: 0.7,
                        borderTop: index > 0
                          ? (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.14)}`
                          : 'none',
                      }}
                    >
                      <Chip
                        size="small"
                        label={modifier.label}
                        color="primary"
                        variant="outlined"
                        sx={{
                          height: 18,
                          fontSize: 9.5,
                          fontWeight: 800,
                          borderRadius: 0.6,
                          flexShrink: 0,
                          mt: 0.15,
                        }}
                      />
                      <Typography sx={{ fontSize: 10.5, color: 'text.secondary', lineHeight: 1.4 }}>
                        {modifier.description}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
              )}
            </Stack>
          </>
        ) : null}

        <Divider />
        <Typography variant="subtitle2">Score Breakdown</Typography>
        <Stack spacing={0.7}>
          {!words?.length ? (
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              Place tiles to preview scoring steps.
            </Typography>
          ) : (
            words.map((word) => {
              const breakdown = word.scoreBreakdown;
              if (!breakdown) {
                return null;
              }

              return (
                <Box
                  key={`${word.key}-breakdown`}
                  sx={{
                    border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.95)}`,
                    borderRadius: 1.7,
                    padding: 0.85,
                    backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.85),
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: 12.5, marginBottom: 0.45 }}>
                    {word.text}: {word.score}
                  </Typography>
                  <Box
                    component="table"
                    sx={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: 10.5,
                      color: 'text.secondary',
                      marginBottom: 0.35,
                      '& th, & td': {
                        borderBottom: (theme) => `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                        textAlign: 'left',
                        padding: '3px 4px',
                        verticalAlign: 'top',
                      },
                      '& th': {
                        fontWeight: 700,
                        color: 'text.primary',
                      },
                    }}
                  >
                    <Box component="thead">
                      <Box component="tr">
                        <Box component="th">Tile</Box>
                        <Box component="th">Base</Box>
                        <Box component="th">Square</Box>
                        <Box component="th">Tile Mod</Box>
                        <Box component="th">Effects</Box>
                        <Box component="th">Total</Box>
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {breakdown.letters.map((letterStep) => (
                        <Box key={`${word.key}-${letterStep.tileId}`} component="tr">
                          <Box component="td">{letterStep.letter}</Box>
                          <Box component="td">{letterStep.base}</Box>
                          <Box component="td">{formatModifierLabels(letterStep.squareModifiers)}</Box>
                          <Box component="td">{formatModifierLabels(letterStep.tileModifiers)}</Box>
                          <Box component="td">{formatEffectLabels(letterStep.activeEffectDetails)}</Box>
                          <Box component="td">{letterStep.letterScore}</Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>
                    Word effects: {formatEffectLabels(breakdown.activeWordEffectDetails)}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary', marginTop: 0.4 }}>
                    Letters {breakdown.letterTotal} × Word {breakdown.wordMultiplier} + Bonus{' '}
                    {breakdown.wordBonus}
                    {breakdown.intrinsicLengthBonus > 0 ? ` + Length ${breakdown.intrinsicLengthBonus}` : ''}{' '}
                    = {breakdown.finalWordScore}
                  </Typography>
                </Box>
              );
            })
          )}
        </Stack>

        {!inspectedRackTile ? (
          <>
            <Divider />
            <Typography variant="subtitle2">History</Typography>
            <Stack spacing={0.6}>
              {!history.length ? (
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  No words submitted yet.
                </Typography>
              ) : null}
              {history.map((entry, index) => (
                <Box key={`${entry.word}-${index}`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontWeight: 600 }}>{entry.word}</Typography>
                  <Typography sx={{ color: 'text.secondary' }}>+{entry.score}</Typography>
                </Box>
              ))}
            </Stack>
          </>
        ) : null}
      </Box>

    </Box>
  );
}

export default ScorePanel;
